import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { LowerCasePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteOrigin,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { FilterCriteria, FilterOperation } from '@ird/shared-base';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ActiveFilter,
  DEFAULT_OPERATIONS,
  FilterFieldDefinition,
  FilterFieldValue,
  FilterPhase,
  FilterSearchParameters,
  OPERATOR_LABELS,
} from './filter-search-input.models';
import { FilterSearchService } from './filter-search.service';

@Component({
  selector: 'shared-filter-search-input',
  templateUrl: './filter-search-input.component.html',
  styleUrls: ['./filter-search-input.component.scss'],
  imports: [
    ReactiveFormsModule,
    LowerCasePipe,
    TranslatePipe,
    MatFormField,
    MatIcon,
    MatAutocomplete,
    MatAutocompleteOrigin,
    MatAutocompleteTrigger,
    MatChipGrid,
    MatChipInput,
    MatChipRow,
    MatChipRemove,
    MatOption,
  ],
})
export class FilterSearchInputComponent implements OnInit {
  @Input() fields: FilterFieldDefinition[] = [];
  @Input() placeholder = '';
  @Input() debounceMs = 0;
  @Input() initialFilters: ActiveFilter[] = [];

  @Output() filtersChanged = new EventEmitter<FilterSearchParameters>();

  inputControl = new FormControl<string>('');
  activeFilters: WritableSignal<ActiveFilter[]> = signal([]);

  phase: FilterPhase = FilterPhase.FIELD;
  pendingField: FilterFieldDefinition | null = null;
  pendingOperation: FilterOperation | null = null;
  /** json fields with a key list: the key picked in the first half of the value phase. */
  pendingJsonKey: FilterFieldValue | null = null;

  filteredFields: WritableSignal<FilterFieldDefinition[]> = signal([]);
  filteredOperations: WritableSignal<FilterOperation[]> = signal([]);
  filteredValues: WritableSignal<FilterFieldValue[]> = signal([]);
  valueErrorKey: WritableSignal<string | null> = signal(null);

  readonly separatorKeyCodes: number[] = [ENTER, COMMA];
  readonly operatorLabels = OPERATOR_LABELS;
  readonly FilterPhase = FilterPhase;

  private readonly destroyRef = inject(DestroyRef);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly translate = inject(TranslateService);
  private readonly filterSearchService = inject(FilterSearchService);

  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  /** Prevents [object Object] from appearing in the input after autocomplete selection */
  displayFn = (): string => '';

  ngOnInit(): void {
    this.filteredFields.set(this.getAvailableFields());

    if (this.initialFilters?.length > 0) {
      this.activeFilters.set([...this.initialFilters]);
    }

    this.inputControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(this.debounceMs), distinctUntilChanged())
      .subscribe((value) => {
        // Autocomplete sets control value to the selected option object briefly before clearInput() runs.
        // Skip non-string values to avoid calling .toLowerCase() on them.
        const text = typeof value === 'string' ? value : '';
        if (this.valueErrorKey()) {
          this.valueErrorKey.set(null);
        }
        switch (this.phase) {
          case FilterPhase.FIELD:
            this.filterFieldSuggestions(text);
            break;
          case FilterPhase.OPERATOR:
            this.filterOperatorSuggestions(text);
            break;
          case FilterPhase.VALUE:
            this.filterValueSuggestions(text);
            break;
        }
      });
  }

  // --- Phase: Field Selection ---

  private filterFieldSuggestions(text: string): void {
    const lower = text.toLowerCase();
    this.filteredFields.set(
      this.getAvailableFields().filter((f) => {
        const translatedLabel = this.translate.instant(f.label);
        return translatedLabel.toLowerCase().includes(lower) || f.field.toLowerCase().includes(lower);
      }),
    );
  }

  onFieldSelected(field: FilterFieldDefinition): void {
    this.pendingField = field;
    const ops = this.getOperations(field);

    if (ops.length === 1) {
      this.pendingOperation = ops[0];
      this.transitionToValuePhase();
    } else {
      this.transitionToOperatorPhase();
    }
  }

  // --- Phase: Operator Selection ---

  private transitionToOperatorPhase(): void {
    this.phase = FilterPhase.OPERATOR;
    this.filteredOperations.set(this.pendingField ? this.getOperations(this.pendingField) : []);
    this.clearInput();
    // Re-clear after autocomplete finishes its internal value update, then open panel
    timer(0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearInput();
        this.focusInput();
        this.autocompleteTrigger?.openPanel();
      });
  }

  private filterOperatorSuggestions(text: string): void {
    if (!this.pendingField) {
      return;
    }
    const lower = text.toLowerCase();
    this.filteredOperations.set(
      this.getOperations(this.pendingField).filter(
        (op) => op.toLowerCase().includes(lower) || OPERATOR_LABELS[op].toLowerCase().includes(lower),
      ),
    );
  }

  onOperatorSelected(operation: FilterOperation): void {
    this.pendingOperation = operation;

    // IS_NULL / IS_NOT_NULL don't need a value
    if (operation === FilterOperation.IS_NULL || operation === FilterOperation.IS_NOT_NULL) {
      this.addFilter(null, operation === FilterOperation.IS_NULL ? 'empty' : 'not empty');
      return;
    }

    this.transitionToValuePhase();
  }

  // --- Phase: Value Selection ---

  private transitionToValuePhase(): void {
    this.phase = FilterPhase.VALUE;
    this.filteredValues.set(this.valueOptions());
    this.clearInput();
    // Re-clear after autocomplete finishes its internal value update, then focus/open panel
    timer(0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearInput();
        this.focusInput();
        if (this.valueOptions().length) {
          this.autocompleteTrigger?.openPanel();
        }
      });
  }

  private filterValueSuggestions(text: string): void {
    const lower = text.toLowerCase();
    this.filteredValues.set(
      this.valueOptions().filter((v) => v.label.toLowerCase().includes(lower) || v.value.toLowerCase().includes(lower)),
    );
  }

  /**
   * A json field with a key list offers its keys first, then the values that key carries — a key
   * without any leaves the list empty, which keeps it free text. Everything else offers plain values.
   */
  private valueOptions(): FilterFieldValue[] {
    if (this.pendingField?.jsonKeys?.length) {
      return this.pendingJsonKey ? (this.pendingJsonKey.values ?? []) : this.pendingField.jsonKeys;
    }
    return this.pendingField?.values ?? [];
  }

  onValueSelected(fieldValue: FilterFieldValue): void {
    // First half of the value phase for a keyed json field: remember the key, then ask for the value.
    if (this.pendingField?.jsonKeys?.length && !this.pendingJsonKey) {
      this.pendingJsonKey = fieldValue;
      this.transitionToValuePhase();
      return;
    }
    // Second half: the pair goes on the wire, not the bare value a non-json field would send.
    if (this.pendingJsonKey) {
      const key = this.pendingJsonKey;
      this.addFilter(JSON.stringify({ [key.value]: fieldValue.value }), `${key.label} = ${fieldValue.label}`);
      return;
    }
    this.addFilter(fieldValue.value, fieldValue.label);
  }

  onValueSubmitted(): void {
    // Don't allow free-text for fields with predefined values (e.g. enums)
    if (this.pendingField?.values?.length) {
      return;
    }
    // Read from the native input to avoid [object Object] from autocomplete's internal value setting
    const text = (this.filterInput?.nativeElement.value ?? this.inputControl.value ?? '').trim();
    if (!text || !this.pendingField || !this.pendingOperation) {
      return;
    }
    if (this.pendingField.type === 'json') {
      if (this.pendingJsonKey) {
        const key = this.pendingJsonKey;
        // A typed label or a differently-cased code still has to reach the wire as the stored code.
        const lower = text.toLowerCase();
        const match = key.values?.find((v) => v.value.toLowerCase() === lower || v.label.toLowerCase() === lower);
        this.addFilter(JSON.stringify({ [key.value]: match?.value ?? text }), `${key.label} = ${match?.label ?? text}`);
        return;
      }
      const encoded = this.filterSearchService.encodeJsonFilterValue(text);
      if (encoded === null) {
        this.valueErrorKey.set('FILTER_SEARCH_JSON_VALUE_INVALID');
        return;
      }
      this.addFilter(encoded, this.filterSearchService.decodeJsonFilterValue(encoded) ?? text);
      return;
    }
    this.addFilter(text, text);
  }

  onEnterKey(event: Event): void {
    if (this.autocompleteTrigger?.panelOpen) {
      return;
    }

    switch (this.phase) {
      case FilterPhase.FIELD:
        // Free-text search: emit current text as search query on Enter
        event.preventDefault();
        this.emitCurrentState();
        break;
      case FilterPhase.VALUE:
        // For string fields in value phase with no predefined values, handle Enter explicitly
        // because MatAutocomplete may intercept it before matChipInputTokenEnd fires
        event.preventDefault();
        this.onValueSubmitted();
        break;
      case FilterPhase.OPERATOR:
        // Operators are always selected via autocomplete, no Enter handling needed
        break;
    }
  }

  onBackspace(): void {
    const inputValue = this.filterInput?.nativeElement.value ?? '';
    // Only go back a step when the input is empty
    if (inputValue) {
      return;
    }

    switch (this.phase) {
      case FilterPhase.FIELD:
        // Already at the first phase, nothing to go back to
        break;
      case FilterPhase.OPERATOR:
        this.resetToFieldPhase();
        break;
      case FilterPhase.VALUE:
        // A picked json key is its own step back, before the operator/field phases.
        if (this.pendingJsonKey) {
          this.pendingJsonKey = null;
          this.transitionToValuePhase();
          break;
        }
        // Go back to operator phase (or field if operator was auto-selected)
        if (this.pendingField && this.getOperations(this.pendingField).length > 1) {
          this.pendingOperation = null;
          this.transitionToOperatorPhase();
        } else {
          this.resetToFieldPhase();
        }
        break;
    }
  }

  // --- Autocomplete option selected (dispatches by phase) ---

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;

    switch (this.phase) {
      case FilterPhase.FIELD:
        this.onFieldSelected(value as FilterFieldDefinition);
        break;
      case FilterPhase.OPERATOR:
        this.onOperatorSelected(value as FilterOperation);
        break;
      case FilterPhase.VALUE:
        this.onValueSelected(value as FilterFieldValue);
        break;
    }
  }

  // --- Active Filter Management ---

  private addFilter(value: string | null, displayLabel: string): void {
    if (!this.pendingField || !this.pendingOperation) {
      return;
    }
    const field = this.pendingField;
    const operation = this.pendingOperation;

    const newFilter: ActiveFilter = {
      field,
      operation,
      displayValue: displayLabel,
      value: value ?? undefined,
    };

    this.activeFilters.update((filters) => [...filters, newFilter]);
    this.resetToFieldPhase();
    this.emitCurrentState();
    this.announcer.announce(`Added filter: ${field.label} ${OPERATOR_LABELS[operation]} ${displayLabel}`);
  }

  removeFilter(index: number): void {
    const removed = this.activeFilters()[index];
    this.activeFilters.update((filters) => filters.filter((_, i) => i !== index));
    this.filteredFields.set(this.getAvailableFields());
    this.emitCurrentState();
    if (removed) {
      this.announcer.announce(`Removed filter: ${removed.field.label}`);
    }
  }

  editFilter(index: number): void {
    const filter = this.activeFilters()[index];
    if (!filter) {
      return;
    }
    // Remove the filter being edited and re-enter field selection flow
    this.activeFilters.update((filters) => filters.filter((_, i) => i !== index));
    this.onFieldSelected(filter.field);
  }

  clearAll(): void {
    this.activeFilters.set([]);
    this.resetToFieldPhase();
    this.emitCurrentState();
  }

  cancelPhase(): void {
    this.resetToFieldPhase();
  }

  // --- Helpers ---

  getPlaceholderText(): string {
    switch (this.phase) {
      case FilterPhase.OPERATOR:
        return this.translate.instant('FILTER_SEARCH_SELECT_OPERATOR_SHORT');
      case FilterPhase.VALUE:
        if (this.pendingField?.type === 'json' && !this.pendingJsonKey) {
          return this.translate.instant(
            this.pendingField.jsonKeys?.length ? 'FILTER_SEARCH_SELECT_JSON_KEY_SHORT' : 'FILTER_SEARCH_ENTER_JSON_VALUE_SHORT',
          );
        }
        return this.translate.instant('FILTER_SEARCH_ENTER_VALUE_SHORT');
      default:
        return this.translate.instant(this.placeholder);
    }
  }

  getOperatorLabel(op: FilterOperation): string {
    return OPERATOR_LABELS[op] ?? op;
  }

  /** Once a JSON key is picked the value reads `KEY = value`, matching the chip. */
  getPendingOperatorLabel(op: FilterOperation): string {
    return this.getOperatorLabel(this.pendingJsonKey ? FilterOperation.EQUALS : op);
  }

  getOperations(field: FilterFieldDefinition): FilterOperation[] {
    return field.operations?.length ? field.operations : DEFAULT_OPERATIONS[field.type];
  }

  /** Returns fields not yet used by active filters */
  private getAvailableFields(): FilterFieldDefinition[] {
    const usedFields = new Set(this.activeFilters().map((f) => f.field.field));
    return this.fields.filter((f) => !usedFields.has(f.field));
  }

  private resetToFieldPhase(): void {
    this.phase = FilterPhase.FIELD;
    this.pendingField = null;
    this.pendingOperation = null;
    this.pendingJsonKey = null;
    this.valueErrorKey.set(null);
    this.clearInput();
    this.filteredFields.set(this.getAvailableFields());
  }

  private clearInput(): void {
    this.inputControl.setValue('', { emitEvent: false });
    if (this.filterInput) {
      this.filterInput.nativeElement.value = '';
    }
  }

  private focusInput(): void {
    this.filterInput?.nativeElement.focus();
  }

  private emitCurrentState(): void {
    const filters: FilterCriteria<Record<string, unknown>>[] = this.activeFilters().map<FilterCriteria<Record<string, unknown>>>((af) => ({
      field: af.field.field,
      operation: af.operation,
      value: af.value,
      values: af.values,
    }));

    const search = this.phase === FilterPhase.FIELD ? (this.inputControl.value ?? '').trim() : '';

    this.filtersChanged.emit({ search, filters });
  }
}
